import { useState, useEffect } from 'react'
import { useGameStore } from '@/store/gameStore'
import { CLUB_LEVELS } from '@/config/clubLevelConfig'
import { getClubHero } from '@/config/campusConfig'
import { motion, AnimatePresence } from 'framer-motion'
import { backdropVariants, SPRING_REVEAL, useMotionVariants, modalVariants } from '../motion/motionTokens'
import { useTopModal } from '../modals/useTopModal'
import { AngledButton } from './broadcast'
import { ModalCloseButton } from './ModalCloseButton'

export function LevelUpModal() {
  const pendingLevelUp = useGameStore(s => s.pendingLevelUp)
  const setPendingLevelUp = useGameStore(s => s.setPendingLevelUp)
  const top = useTopModal()
  const modal = useMotionVariants(modalVariants)
  const backdrop = useMotionVariants(backdropVariants)
  const [heroOk, setHeroOk] = useState(true)

  const level = pendingLevelUp !== null
    ? CLUB_LEVELS.find(l => l.level === pendingLevelUp)
    : null
  const hero = level ? getClubHero(level.level) : null

  useEffect(() => { setHeroOk(true) }, [hero?.image])

  return (
    <AnimatePresence>
      {level && top === 'levelUp' && (
        <>
          <motion.div
            variants={backdrop}
            initial="initial"
            animate="enter"
            exit="exit"
            style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.7)', zIndex: 200 }}
          />

          <motion.div
            variants={modal}
            initial="initial"
            animate="enter"
            exit="exit"
            className="pointer-events-auto"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              x: '-50%',
              y: '-50%',
              zIndex: 201,
              background: 'var(--bg-surface)',
              border: '1px solid color-mix(in srgb, var(--gold) 35%, transparent)',
              borderRadius: '16px',
              overflow: 'hidden auto',
              maxHeight: '90dvh',
              width: 'min(440px, 92vw)',
              textAlign: 'center',
              boxShadow: 'var(--shadow-lg), inset 0 3px 0 0 var(--gold)',
            }}
          >
            <ModalCloseButton onClick={() => setPendingLevelUp(null)} />
            {/* The new club itself is the celebration */}
            <motion.div
              initial={{ scale: 1.12, filter: 'brightness(1.5)' }}
              animate={{ scale: 1, filter: 'brightness(1)' }}
              transition={{ ...SPRING_REVEAL, delay: 0.1 }}
              style={{ position: 'relative', aspectRatio: '16 / 8', background: 'var(--bg-primary)' }}
            >
              {hero && heroOk ? (
                <img
                  src={hero.image}
                  alt={level.name}
                  onError={() => setHeroOk(false)}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              ) : (
                <div style={{
                  width: '100%', height: '100%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', fontSize: '3rem',
                }}>
                  {level.emoji}
                </div>
              )}
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(180deg, transparent 40%, rgba(13,21,32,0.9) 100%)',
                display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                padding: '0.9rem 1.25rem', textAlign: 'left',
              }}>
                <div style={{
                  fontSize: '0.72rem', color: 'var(--gold)',
                  letterSpacing: '2.5px', textTransform: 'uppercase', fontWeight: 700,
                }}>
                  Nivel {level.level} alcanzado
                </div>
                <div style={{
                  fontSize: '1.5rem', fontWeight: 800, color: '#fff',
                  fontFamily: 'var(--font-titles)',
                  textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                }}>
                  {level.name}
                </div>
              </div>
            </motion.div>

            <div style={{ padding: '1.25rem 1.5rem 1.5rem' }}>
            <div style={{
              fontSize: '0.875rem',
              color: 'var(--text-secondary)',
              marginBottom: '1.25rem',
            }}>
              {level.description}
            </div>

            {(level.reward.money > 0 || level.reward.reputation > 0) && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                style={{
                  background: 'color-mix(in srgb, var(--success) 10%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--success) 30%, transparent)',
                  borderRadius: '8px',
                  padding: '0.6rem',
                  marginBottom: '1rem',
                }}
              >
                <div className="section-label" style={{ marginBottom: '0.3rem' }}>Recompensa</div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem' }}>
                  {level.reward.money > 0 && (
                    <span style={{ color: 'var(--success)', fontWeight: 700 }}>
                      +{level.reward.money.toLocaleString('es-ES')}€
                    </span>
                  )}
                  {level.reward.reputation > 0 && (
                    <span style={{ color: 'var(--warning)', fontWeight: 700 }}>
                      +{level.reward.reputation} rep
                    </span>
                  )}
                </div>
              </motion.div>
            )}

            {level.unlockDescriptions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                style={{
                  background: 'color-mix(in srgb, var(--gold) 8%, transparent)',
                  border: '1px solid color-mix(in srgb, var(--gold) 20%, transparent)',
                  borderRadius: '8px',
                  padding: '0.6rem',
                  marginBottom: '1.25rem',
                  textAlign: 'left',
                }}
              >
                <div className="section-label" style={{ marginBottom: '0.4rem' }}>Desbloqueos</div>
                {level.unlockDescriptions.map((desc, i) => (
                  <div key={i} style={{
                    fontSize: '0.8125rem',
                    color: 'var(--gold)',
                    padding: '0.15rem 0',
                  }}>
                    + {desc}
                  </div>
                ))}
              </motion.div>
            )}

            <AngledButton tone="gold" block onClick={() => setPendingLevelUp(null)}>
              Continuar
            </AngledButton>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
